import { FC, Fragment, useEffect, useMemo } from 'react';
import { Divider } from 'antd';
import { getMapBy } from '../../../common/utils';
import { useSystemClassifierSelectOptions } from '../../../configuration';
import { useAlgorithmFormValues } from '../useAlgorithmFormValues';
import { AlgorithmJoinFormFields } from './AlgorithmJoinFormFields';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { useUsedAlgorithmSourceIds } from './useUsedAlgorithmSourceIds';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';

export interface AlgorithmJoinsFormProps {
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  isDbSources: boolean;
}

export const AlgorithmJoinsForm: FC<AlgorithmJoinsFormProps> = (props) => {
  const { sources, mappings, isDbSources } = props;
  const { JOIN_TYPE: joinTypeOptions } = useSystemClassifierSelectOptions(
    ['JOIN_TYPE'],
    'code',
  );

  const algorithmSourcesByIds = useMemo(
    () => getMapBy(sources, 'id'),
    [sources],
  );

  return (
    <AlgorithmJoinsFormStore.Provider
      initialData={{
        sources,
        mappings,
        algorithmSourcesByIds,
        showAutoJoin: false,
        joinTypeOptions,
        isDbSources,
      }}
    >
      <AlgorithmJoinsFormInner />
    </AlgorithmJoinsFormStore.Provider>
  );
};

export const AlgorithmJoinsFormInner: FC = () => {
  const [algorithmJoins] = useAlgorithmFormValues(['joins'] as const);

  useDetectAutoJoinOpportunity();

  return (
    <>
      {algorithmJoins.map((_algorithmJoin, index, { length }) => (
        <Fragment key={index}>
          {index > 0 && <Divider />}
          <AlgorithmJoinFormFields
            index={index}
            count={length}
            isLast={index === length - 1}
          />
        </Fragment>
      ))}
    </>
  );
};

/**
 * Observes which algorithm sources are used in the algorithm joins and if it detects that
 * the same data object description-based source is used more than once, it sets the `showAutoJoin` flag to `true`.
 * `showAutoJoin` flag is in turn consumed by the AutoJoinButton component to show/hide the auto-join button.
 */
const useDetectAutoJoinOpportunity = () => {
  const [algorithmSourcesByIds] = AlgorithmJoinsFormStore.useValue(
    'algorithmSourcesByIds',
  );
  const usedAlgorithmSourceIds = useUsedAlgorithmSourceIds();
  const [, setShowAutoJoin] = AlgorithmJoinsFormStore.useValue('showAutoJoin');

  useEffect(() => {
    const seenDataObjectDescriptionCodes = new Set<string>();

    for (const algorithmSourceId of usedAlgorithmSourceIds) {
      const algorithmSource = algorithmSourcesByIds.get(algorithmSourceId);

      if (!algorithmSource) {
        continue;
      }

      const { sourceType, sourceCode } = algorithmSource;

      if (sourceType === 'DB') {
        continue;
      }

      if (seenDataObjectDescriptionCodes.has(sourceCode)) {
        setShowAutoJoin(true);
        return;
      }
      seenDataObjectDescriptionCodes.add(sourceCode);
    }

    setShowAutoJoin(false);
  }, [algorithmSourcesByIds, usedAlgorithmSourceIds, setShowAutoJoin]);
};
